import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Users, BarChart3 } from 'lucide-react';

const faqData = [
  {
    question: "What is AI workflow automation?",
    answer: "AI workflow automation combines rule-based automation (handling repetitive tasks) with artificial intelligence (understanding context, generating content, making recommendations) to streamline business operations. It reduces manual work, improves accuracy, and lets teams focus on higher-value activities."
  },
  {
    question: "Is AI automation suitable for small businesses?",
    answer: "Yes. Modern AI tools are accessible and affordable for SMEs. Projects start from $900 for discovery and can deliver measurable ROI within weeks. You don't need a large IT team or technical expertise."
  },
  {
    question: "How do you ensure responsible AI use?",
    answer: "We follow a human-in-the-loop approach where critical decisions retain human review. Our governance framework includes the SAFE-NZ data handling principles, compliance with the NZ Privacy Act 2020, and rollback capability on every deployment."
  },
  {
    question: "What tasks can be automated?",
    answer: "Common automations include customer enquiry handling, appointment scheduling, document collection, reporting, invoicing, follow-up sequences, client onboarding, and internal knowledge management."
  },
  {
    question: "How long does implementation take?",
    answer: "Most initial projects are live within 2–4 weeks. Complex integrations may take longer. Every deployment includes structured 30/60/90 day reviews."
  },
  {
    question: "Do you work with businesses outside the main centres?",
    answer: "Yes. We serve businesses across New Zealand including regional areas like Marlborough, with remote discovery and implementation capability."
  }
];

const AIWorkflowAutomationNZ: React.FC = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI & Workflow Automation for New Zealand Small Businesses",
    "provider": {
      "@type": "Organization",
      "name": "ThinkTomorrow.AI",
      "url": "https://www.thinktomorrow.ai"
    },
    "description": "AI and workflow automation consulting for small and medium businesses across New Zealand.",
    "areaServed": { "@type": "Country", "name": "New Zealand" },
    "serviceType": "AI & Workflow Automation Consulting"
  };

  return (
    <>
      <Helmet>
        <title>AI & Workflow Automation NZ | Small Business | ThinkTomorrow.AI</title>
        <meta name="description" content="AI and workflow automation for New Zealand small businesses. Reduce admin, automate operations, implement responsibly. Discovery from $900." />
        <link rel="canonical" href="https://www.thinktomorrow.ai/ai-workflow-automation-nz" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader
          title="AI & Workflow Automation for New Zealand Small Businesses"
          subtitle="Practical, responsible automation that saves time, reduces errors, and grows with your business."
        />

        {/* What It Means */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">What AI & Workflow Automation Actually Means</h2>
            <p className="text-muted-foreground text-lg mb-4">
              Workflow automation uses technology to handle repetitive business tasks — sending follow-ups, collecting documents, updating records, generating reports. AI adds a layer of intelligence: understanding context, summarising information, answering questions, and making recommendations.
            </p>
            <p className="text-muted-foreground text-lg">
              Together, they let small businesses operate with the efficiency of much larger organisations — without the overhead.
            </p>
          </div>
        </section>

        {/* Tasks We Automate */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Business Tasks We Automate</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Customer enquiry handling",
                "Appointment scheduling & reminders",
                "Document collection & requests",
                "Client onboarding workflows",
                "Weekly & monthly reporting",
                "Invoice follow-ups",
                "Internal knowledge assistants",
                "Email & communication sequences",
                "Data entry & record updates"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-background rounded-lg border">
                  <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                  <span className="text-foreground text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Industries We Support</h2>
            <p className="text-muted-foreground text-lg mb-6">
              We work with SMEs across New Zealand, with particular expertise in professional service firms — architects, engineers, surveyors, accountants, and consultants.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ai-automation-professional-services" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal/10 text-brand-teal rounded-lg hover:bg-brand-teal/20 transition-colors font-medium text-sm">
                <Users size={16} /> Professional Services <ArrowRight size={14} />
              </Link>
              <Link to="/workflow-automation-marlborough" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal/10 text-brand-teal rounded-lg hover:bg-brand-teal/20 transition-colors font-medium text-sm">
                Marlborough & Blenheim <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* Responsible Method */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Our Responsible Implementation Method</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="p-6 bg-background rounded-lg border">
                <Shield className="text-brand-teal mb-3" size={24} />
                <h3 className="font-semibold text-foreground mb-2">Human Oversight Built In</h3>
                <p className="text-muted-foreground text-sm">Critical decisions always retain human review. Every deployment includes rollback capability and governance documentation.</p>
              </div>
              <div className="p-6 bg-background rounded-lg border">
                <BarChart3 className="text-brand-teal mb-3" size={24} />
                <h3 className="font-semibold text-foreground mb-2">Measurable Outcomes</h3>
                <p className="text-muted-foreground text-sm">Baseline metrics captured before deployment. Structured 30/60/90 day reviews track ROI and identify optimisation opportunities.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {["Discovery", "Roadmap", "Implementation", "Optimisation"].map((step, i) => (
                <div key={i} className="text-center p-4 bg-background rounded-lg border">
                  <div className="w-8 h-8 rounded-full bg-brand-teal text-white flex items-center justify-center font-bold mx-auto mb-2 text-sm">{i + 1}</div>
                  <span className="font-medium text-foreground text-sm">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Case Study */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Featured Case Study</h2>
            <p className="text-gray-300 mb-6">See how a New Zealand architecture practice used workflow intelligence to reduce coordination overhead and improve project visibility.</p>
            <Link to="/case-studies/architect-workflow-intelligence">
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-brand-navy flex items-center gap-2">
                Read: Architectural Workflow Intelligence <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Automate Your Business?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Start with a discovery session. We'll identify your highest-impact automation opportunities and build a practical roadmap.</p>
            <Link to="/contact">
              <Button variant="primary" className="flex items-center gap-2 mx-auto">
                Book Discovery Call <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h3 className="font-semibold text-foreground mb-4">Related Pages</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/workflow-automation-marlborough" className="text-brand-teal hover:underline text-sm">Workflow Automation Marlborough →</Link>
              <Link to="/ai-automation-professional-services" className="text-brand-teal hover:underline text-sm">AI Automation for Professional Services →</Link>
              <Link to="/case-studies/architect-workflow-intelligence" className="text-brand-teal hover:underline text-sm">Case Study: Architect Workflow Intelligence →</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AIWorkflowAutomationNZ;
